<template>
  <v-card width="94%" elevation="0" class="ml-5">
    <v-container>
      <v-row>
        <v-textarea
          v-on:keyup.enter="$event.stopPropagation()"
          style="font-family: monospace"
          v-model="TLEText"
          label="TLE"
          full-width
          outlined
          rows="3"
          class="mb-n7"
          :readonly="textboxReadonly"
        ></v-textarea>
      </v-row>
      <v-row>
        <ExpandableTLECard
          :title="'Show details'"
          :root="_self"
          :isExpanded="isExpanded"
          :smaller="true"
          :values="value"
          :error="error"
          :disabled="expandDisabled"
          v-on:expand="updateExpand($event)"
        ></ExpandableTLECard>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import ExpandableTLECard from './ExpandableTLECard.vue'

export default {
  name: 'TLEDecoder',

  props: {
    tle: {
      type: Array,
      default: null
    },
    isExpanded: {
      type: Boolean,
      default: true
    },
    expandDisabled: {
      type: Boolean,
      default: true
    },
    textboxReadonly: {
      type: Boolean,
      default: false
    }
  },

  components: {
    ExpandableTLECard
  },

  data() {
    return {
      TLEText: '',
      value: null,
      error: null
    }
  },

  mounted() {},

  watch: {
    async TLEText(input) {
      if (input == '') {
        this.value = null
        this.error = null
        this.$emit('update:tle', null)
        return
      }
      const { tle, namedValues } = this.getNamedValuesFromText(input)
      if (!namedValues) {
        this.value = null
        this.error = 'Invalid TLE'
        this.$emit('update:tle', null)
      } else {
        this.value = await this.getValueFromServer(tle)
        if (this.value != null) {
          this.value = { ...this.value, ...namedValues }
          this.value.launchdetails = this.formatLaunchDetails(
            namedValues.launchNb,
            namedValues.launchYear,
            namedValues.launchCode
          )
          this.error = null
          this.$emit('updateDate', this.value.orbitDateTime)
          this.$emit('update:tle', tle)
        } else {
          this.$emit('update:tle', null)
        }
      }
    },
    tleWatcher: {
      handler(newValue) {
        if (newValue == null && this.error == null) {
          this.TLEText = ''
        } else if (newValue != null && newValue.length > 1) {
          this.TLEText = newValue.reduce(
            (ligne1, ligne2) => ligne1 + '\n' + ligne2
          )
        }
      },
      immediate: true
    }
  },

  computed: {
    tleWatcher() {
      return this.tle
    }
  },

  methods: {
    formatLaunchDetails(launchNb, launchYear, launchCode) {
      const formattedLaunchYear =
        launchYear > 56 ? `19${launchYear}` : `20${launchYear}`

      const english_ordinal_rules = new Intl.PluralRules('en', {
        type: 'ordinal'
      })
      const suffixes = {
        one: 'st',
        two: 'nd',
        few: 'rd',
        other: 'th'
      }
      const suffix = suffixes[english_ordinal_rules.select(launchNb)]

      const lettersToValue = launchCode
        .trim()
        .split('')
        .reverse()
        .map((letter, index) => {
          const letterRank = letter.charCodeAt(0) - 64
          let letterValue = letterRank
          if (letterRank > 10) letterValue-- //I is never used, so the value must be offset by 1 for any letter afterward
          if (letterRank > 15) letterValue-- //O is never used, so the value must be offset by 1 for any letter afterward
          return Math.pow(24, index) * letterValue
        })
        .reduce((partialSum, a) => partialSum + a, 0)
      return `${Number(
        launchNb
      )}${suffix}. launch of ${formattedLaunchYear}, piece ${lettersToValue}`
    },

    getNamedValuesFromText(TLEText) {
      const regex_tle =
        /^\s*(?:(?<name>.{1,24})\s*\n)?\s*(?<l1>.{69})\s*\n\s*(?<l2>.{69})\s*$/
      const regex_l1 =
        /^1\s(?<number>.{5})(?<classification>.)\s(?<launchYear>.{2})(?<launchNb>.{3})(?<launchCode>.{3})\s(?:.{2})(?:.{12})\s(?:.{10})\s(?:.{8})\s(?:.{8})\s(?:.)\s(?:.{5})$/
      const regex_l2 =
        /^2\s(?:.{5})\s(?:.{8})\s(?:.{8})\s(?:.{7})\s(?:.{8})\s(?:.{8})\s(?:.{11})(?<revcount>.{5})(?:.)$/
      const lines = TLEText.match(regex_tle)?.groups
      const l1Values = lines ? lines.l1.match(regex_l1)?.groups : null
      const l2Values = lines ? lines.l2.match(regex_l2)?.groups : null
      return {
        tle: lines ? [lines.l1, lines.l2] : null,
        namedValues:
          lines && l1Values && l2Values
            ? {
                ...l1Values,
                ...l2Values,
                name: lines.name ? lines.name : null
              }
            : null
      }
    },
    getValueFromServer(tle) {
      return this.$http
        .post('/physics/tledecoder', { tle: tle })
        .then(function (response) {
          return {
            ...response.data.osculatingOrbit,
            frame: response.data.frameDescription
          }
        })
        .catch((e) => {
          if (
            e.response &&
            e.response.status === 400 &&
            e.response.data.startsWith('Invalid definition')
          ) {
            this.error = 'Invalid TLE'
          } else {
            this.error =
              'An error occured while trying to communicate with the server'
          }

          return null
        })
    },
    updateExpand(isExpanded) {
      this.$emit('expand', isExpanded)
    }
  }
}
</script>
